import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, StarIcon, XIcon, PlayIcon, ShieldCheckIcon } from 'lucide-react';
import { useMetaPixel, getActiveCtaPixelIds } from '../hooks/useMetaPixel';
import { LegalLinks } from '../components/LegalLinks';

// YouTube-compliant landing page (/yt). Same brand look as /demo but written to
// pass YouTube ad review: no price anchor, no scarcity counter, no income
// claims — with a clear "who it's NOT for", module breakdown, FAQ and legal
// disclaimer.
const CHECKOUT_URL = 'https://commas.com/checkout/gQ88lpN7PKuMEe';
const PANDA_ORIGIN = 'https://player-vz-cf0b871f-7fe.tv.pandavideo.com';
const PANDA_BASE = `${PANDA_ORIGIN}/embed/?v=`;
const PANDA_ID = '0a7e74ce-c106-44a5-9484-3931fa75bddc';
const PANDA_SRC = PANDA_BASE + PANDA_ID;

/**
 * PERF: video facade. The PandaVideo embed is the single heaviest asset on the
 * page (full third-party player: JS, CSS, HLS manifest, segments). Loading it
 * on mount blocks the hero and competes with everything else for bandwidth.
 *
 * Instead we render a near-zero-cost placeholder and only inject the real
 * iframe on the first interaction — at which point it autoplays, so the
 * visitor still lands straight in the video with a single click.
 */
function HeroVideo() {
  const [activated, setActivated] = useState(false);

  // Warm up the DNS/TLS handshake to the video host while the visitor reads the
  // headline, so the player starts streaming the instant they hit play.
  useEffect(() => {
    const hints: HTMLLinkElement[] = [];
    for (const rel of ['preconnect', 'dns-prefetch']) {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = PANDA_ORIGIN;
      if (rel === 'preconnect') link.crossOrigin = '';
      document.head.appendChild(link);
      hints.push(link);
    }
    return () => hints.forEach(l => l.remove());
  }, []);
  return <div className="relative w-full overflow-hidden rounded-2xl bg-black shadow-[0_12px_40px_rgba(0,0,0,0.18)]" data-id="element-925" data-mp="3opf71u696qqe">
      <div className="relative w-full" style={{
      paddingTop: '56.25%'
    }} data-id="element-926" data-mp="34nu5f22ubg6b">
        {activated ? <iframe id={`panda-${PANDA_ID}`} src={`${PANDA_SRC}&autoplay=1`} title="Academia Heylloyd — Mira este video primero" className="absolute inset-0 w-full h-full border-0" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture" allowFullScreen data-id="element-927" data-mp="30l7c0wqitn70" /> : <button type="button" onClick={() => setActivated(true)} aria-label="Reproducir el video" className="group absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0d1117] cursor-pointer" data-id="element-928" data-mp="2lugk7ooxio3w">
            <span className="inline-flex items-center justify-center w-[68px] h-[68px] rounded-full bg-[#1877F2] shadow-[0_6px_20px_rgba(24,119,242,0.5)] transition-transform duration-150 group-hover:scale-105" data-id="element-929" data-mp="27yml1xdyjd3z">
              <PlayIcon className="w-8 h-8 text-white fill-white translate-x-[2px]" data-id="element-930" data-mp="63lyb6fwq791" />
            </span>
            <span className="text-white/90 text-[14px] font-semibold" data-id="element-931" data-mp="dn3kkrkjdb9a">
              Ver el video
            </span>
          </button>}
      </div>
    </div>;
}
const MODULES: {
  n: string;
  title: string;
  sub: string;
}[] = [{
  n: 'Módulo 1',
  title: 'Fundamentos del negocio',
  sub: 'Cómo funciona el marketing de respuesta directa, qué es una oferta y por qué la mayoría falla antes de empezar.'
}, {
  n: 'Módulo 2',
  title: 'Elegir y validar tu oferta',
  sub: 'Cómo identificar qué vende en tu mercado y cómo probarlo sin gastar de más.'
}, {
  n: 'Módulo 3',
  title: 'Tu funnel y tus páginas',
  sub: 'Cómo montar las páginas de venta, el checkout y la secuencia de seguimiento.'
}, {
  n: 'Módulo 4',
  title: 'Creativos y copy',
  sub: 'Cómo escribir anuncios y guiones que la gente detenga a ver, y cómo estructurarlos.'
}, {
  n: 'Módulo 5',
  title: 'Publicidad pagada',
  sub: 'Cómo lanzar campañas, leer tus métricas y optimizar con base en datos, no en corazonadas.'
}, {
  n: 'Módulo 6',
  title: 'Escalar con orden',
  sub: 'Cuándo subir presupuesto, qué medir y cómo evitar los errores que queman dinero.'
}];
const FOR_YOU = ['Quieres aprender una habilidad real y estás dispuesto a practicarla.', 'Puedes dedicarle tiempo constante cada semana.', 'Tienes presupuesto para las herramientas básicas desde el día uno.'];
const NOT_FOR_YOU = ['Buscas ingresos rápidos o garantizados.', 'Esperas resultados sin invertir tiempo ni presupuesto en publicidad.', 'Quieres algo automático que funcione sin tu trabajo.'];
const TESTIMONIALS = ['Es el primer programa que encontré en español que explica el proceso completo sin saltarse pasos.', 'La comunidad y el soporte hacen la diferencia. Cuando me trabé, tuve respuesta.', 'Las plantillas me ahorraron semanas de estar armando todo desde cero.'];
const FAQS: {
  q: string;
  a: string;
}[] = [{
  q: '¿Necesito experiencia previa?',
  a: 'No. El contenido empieza desde los fundamentos y avanza paso a paso.'
}, {
  q: '¿Cuánto tiempo necesito dedicarle?',
  a: 'Depende de ti. Los miembros que avanzan más rápido le dedican tiempo constante cada semana. No hay atajos.'
}, {
  q: '¿El acceso de por vida es real?',
  a: 'Sí. Un solo pago de $27, sin mensualidades por el contenido, e incluye las actualizaciones futuras.'
}, {
  q: '¿Esto garantiza que voy a ganar dinero?',
  a: 'No. Ningún programa educativo puede garantizarlo, y desconfía del que lo prometa. Te damos el conocimiento y el método; los resultados dependen de tu ejecución, tu presupuesto y tu mercado.'
}, {
  q: '¿Hay reembolso?',
  a: '[Coloca aquí tu política real — plazo exacto y cómo solicitarla.]'
}, {
  q: '¿Cómo contacto con soporte?',
  a: '[Email de soporte visible.]'
}];

// Shared CTA button used across the page.
function CtaButton({
  onClick,
  className = ''
}: {
  onClick: (e: React.MouseEvent) => void;
  className?: string;
}) {
  return <a href="#" onClick={onClick} aria-label="Obtener acceso — $27" className={`inline-flex w-full max-w-[460px] items-center justify-center gap-2.5 rounded-lg bg-[#1877F2] hover:bg-[#166fe0] px-6 py-4 text-white font-extrabold text-[18px] sm:text-[20px] tracking-tight transition-colors duration-150 shadow-[0_2px_8px_rgba(24,119,242,0.35)] ${className}`} data-id="element-932" data-mp="31woz5w06jbr">
      Obtener acceso — $27
    </a>;
}
export function YtPage() {
  const {
    trackCustomEvent,
    trackStandardEvent,
    trackStandardEventTo
  } = useMetaPixel();
  const PRODUCT_DATA = {
    content_name: 'Academia Heylloyd',
    content_ids: ['academia-heylloyd-27'],
    content_type: 'product',
    value: 27,
    currency: 'USD'
  };

  // Fire ViewContent once on mount (PageView fires on pixel init).
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    trackStandardEvent('ViewContent', PRODUCT_DATA);
    trackCustomEvent('YtView');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleCheckout = (source: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    for (const ctaPixel of getActiveCtaPixelIds()) {
      trackStandardEventTo(ctaPixel, 'PageView');
      trackStandardEventTo(ctaPixel, 'ViewContent', PRODUCT_DATA);
      trackStandardEventTo(ctaPixel, 'AddToCart', PRODUCT_DATA);
    }
    trackCustomEvent('YtCtaClick', {
      source
    });
    window.location.href = CHECKOUT_URL;
  };
  return <div className="min-h-screen flex flex-col bg-[#f0f2f5] text-[#1c1e21] font-['system-ui','-apple-system','BlinkMacSystemFont','Segoe_UI','Roboto','Helvetica','Arial','sans-serif']" data-id="element-933" data-mp="3g4s42kq2g069">
      {/* HEADER — founder trust row */}
      <header className="w-full bg-white border-b border-[#e4e6eb]" data-id="element-934" data-mp="1lrk46en8gydr">
        <div className="w-full max-w-[760px] mx-auto px-5 py-3 flex items-center justify-center gap-3" data-id="element-935" data-mp="1qptzqqcve0zw">
          <img src="https://cdn.magicpatterns.com/uploads/3N8o444mVrfs4q2MWiGfdP/Screenshot_2026-07-02_at_3.43.21_PM.png" alt="Gabriel Beltrán — Fundador" width={36} height={36} decoding="async" {...{
          fetchPriority: 'high'
        } as any} className="w-9 h-9 rounded-full object-cover shrink-0" data-id="element-936" data-mp="3slb0xo2ei0gi" />
          <div className="min-w-0" data-id="element-937" data-mp="39moyo5v5e3b0">
            <div className="flex items-center gap-1" data-id="element-938" data-mp="39nqm79tcoi9m">
              <span className="font-bold text-[13px] leading-tight text-[#050505] truncate" data-id="element-939" data-mp="34ox9hjlw0vai">
                Gabriel Beltrán — Fundador, Heylloyd
              </span>
              <span className="shrink-0 inline-flex items-center justify-center w-[13px] h-[13px] rounded-full bg-[#1877F2]" data-id="element-940" data-mp="3iltx7c6plwn6">
                <CheckIcon className="w-2 h-2 text-white" strokeWidth={3} data-id="element-941" data-mp="xxehlrolhp2p" />
              </span>
            </div>
            <p className="text-[11px] text-[#65676b] leading-tight flex items-center gap-1" data-id="element-942" data-mp="tx44zw6as0ev">
              <StarIcon className="w-3 h-3 text-[#F5B301] fill-[#F5B301]" data-id="element-943" data-mp="11flq6znjd3no" />
              <span className="font-bold text-[#050505]" data-id="element-944" data-mp="16eolhsdprvig">4.8</span> · más de
              1,600 miembros
            </p>
          </div>
        </div>
      </header>

      <main className="w-full flex-1" data-id="element-945" data-mp="rjicqxn8hilf">
        {/* HERO */}
        <section className="w-full bg-white" data-id="element-946" data-mp="wfz400pqcd2q">
          <div className="w-full max-w-[760px] mx-auto px-5 pt-10 pb-8 text-center" data-id="element-947" data-mp="wiv1jnxo0zqn">
            <h1 className="font-['Anton'] uppercase tracking-[0.5px] leading-[1.06] text-[clamp(28px,6vw,44px)] text-[#050505]" data-id="element-948" data-mp="1k74st5dnefp6">
              Academia Heylloyd: aprende marketing digital en español, de
              principio a fin.
            </h1>
            <p className="mt-4 text-[17px] font-semibold text-[#1877F2]" data-id="element-949" data-mp="1ff4lgrv90kik">
              Acceso completo por $27. Un solo pago, acceso de por vida.
            </p>
            <p className="mt-3 text-[15px] leading-[1.5] text-[#65676b] max-w-[620px] mx-auto" data-id="element-950" data-mp="2334czu0dwey7">
              Un programa práctico donde aprendes a construir, lanzar y operar
              un negocio de marketing digital: desde elegir tu oferta hasta
              correr tus propios anuncios.
            </p>

            {/* HERO VIDEO — click-to-load facade (see HeroVideo) */}
            <div className="mt-7" data-id="element-951" data-mp="14mdfsamlevbx">
              <HeroVideo data-id="element-952" data-mp="19lzoq7ve1pex" />
            </div>
            <p className="mt-4 text-[14px] leading-[1.5] text-[#65676b] max-w-[620px] mx-auto" data-id="element-953" data-mp="19komichlcppx">
              Antes de comprar, quiero que sepas exactamente qué estás
              recibiendo. En este video te explico qué incluye la academia, cómo
              funciona el modelo de negocio que enseño, qué necesitas para
              operarlo, para quién es esto y —igual de importante— para quién no.
            </p>

            <div className="mt-6 flex justify-center" data-id="element-954" data-mp="qvqbm2y2cd8g">
              <CtaButton onClick={handleCheckout('hero')} data-id="element-955" data-mp="bxxytijwi2xy" />
            </div>
            <p className="mt-3 text-[12px] text-[#8a8d91]" data-id="element-956" data-mp="gxatuqkxegbm">
              Pago único · Acceso inmediato · Contenido 100% en español
            </p>
          </div>
        </section>

        {/* PASO 2 — MODULES */}
        <section className="w-full bg-white" data-id="element-957" data-mp="3pftd7rq5yvs6">
          <div className="w-full max-w-[760px] mx-auto px-5 py-12" data-id="element-958" data-mp="3ph4ixx366dlx">
            <h2 className="text-center text-[clamp(20px,4.4vw,26px)] font-bold leading-[1.2]" data-id="element-959" data-mp="7aq4zy4wi3nj">
              <span className="text-[#1877F2]" data-id="element-960" data-mp="2ac4r29jjy02">Paso 2:</span>{' '}
              <span className="text-[#050505]" data-id="element-961" data-mp="gad7yoda3hip">Qué vas a aprender adentro</span>
            </h2>
            <div className="mt-7 grid gap-4 sm:grid-cols-2" data-id="element-962" data-mp="c8rp8me2c90l">
              {MODULES.map(m => <div key={m.n} className="rounded-xl border border-[#e4e6eb] p-5 bg-[#f7f8fa]" data-id="element-963" data-mp="q06idm1opqld">
                  <p className="text-[12px] font-bold uppercase tracking-wide text-[#1877F2]" data-id="element-964" data-mp="l2eerurogg1c">
                    {m.n}
                  </p>
                  <h3 className="mt-1 text-[17px] font-bold text-[#050505]" data-id="element-965" data-mp="zvzwh9v1xs59">
                    {m.title}
                  </h3>
                  <p className="mt-1.5 text-[14px] leading-[1.5] text-[#65676b]" data-id="element-966" data-mp="1238v1ibqojw3">
                    {m.sub}
                  </p>
                </div>)}
            </div>
            <div className="mt-7 rounded-xl border border-[#d0e3ff] bg-[#eef5ff] p-5" data-id="element-967" data-mp="1c0x1v6mmdhum">
              <p className="text-[14px] font-bold text-[#050505]" data-id="element-968" data-mp="1grdguv639bj3">
                Incluido también:
              </p>
              <ul className="mt-2 space-y-1.5 text-[14px] text-[#3a3b3c]" data-id="element-969" data-mp="1lqzu34lx2rrw">
                {['Plantillas y guiones descargables', 'Comunidad privada de miembros', 'Todas las actualizaciones futuras, sin costo adicional'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-970" data-mp="1zp6jrxgpplgh">
                    <CheckIcon className="w-4 h-4 text-[#1877F2] shrink-0 mt-0.5" data-id="element-971" data-mp="ddihomw5txy" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* PASO 3 — REAL COST */}
        <section className="w-full max-w-[760px] mx-auto px-5 py-12" data-id="element-972" data-mp="3tzmrsqx5xct5">
          <h2 className="text-center text-[clamp(20px,4.4vw,26px)] font-bold leading-[1.2]" data-id="element-973" data-mp="3p73ii65id3u5">
            <span className="text-[#1877F2]" data-id="element-974" data-mp="3k7h3br64ddip">Paso 3:</span>{' '}
            <span className="text-[#050505]" data-id="element-975" data-mp="3k5xo83w798ii">
              Costo real de operar este negocio
            </span>
          </h2>
          <p className="mt-3 text-center text-[15px] text-[#65676b]" data-id="element-976" data-mp="gs38cr684fup">
            Cuentas claras conservan la amistad. 😊
          </p>
          <div className="mt-6 space-y-4 text-[15px] leading-[1.6] text-[#3a3b3c] max-w-[640px] mx-auto" data-id="element-977" data-mp="bt03p7u5obn9">
            <p data-id="element-978" data-mp="6rj37dfl2r77">
              Los <strong data-id="element-979" data-mp="6v6xiesurhmz">$27</strong> te dan acceso al contenido completo de la
              academia. Eso es todo lo que nos pagas a nosotros.
            </p>
            <p data-id="element-980" data-mp="2rke6d3h3dauk">
              Pero para operar un negocio de marketing digital vas a necesitar
              tus propias herramientas: una plataforma para tus páginas y
              presupuesto para publicidad. Eso arranca alrededor de{' '}
              <strong data-id="element-981" data-mp="f7gp47dvx1tf">$40 al mes</strong> y va directo a tus proveedores, no a
              nosotros.
            </p>
            <p data-id="element-982" data-mp="5bdpb86i9f9n">
              Te lo decimos antes de que compres, no después. Si ahora mismo no
              puedes cubrir ese costo operativo, esto todavía no es para ti — y
              preferimos que lo sepas hoy.
            </p>
          </div>
        </section>

        {/* WHO IT'S FOR / NOT FOR */}
        <section className="w-full bg-white" data-id="element-983" data-mp="12zfge0tqb7s7">
          <div className="w-full max-w-[760px] mx-auto px-5 py-12" data-id="element-984" data-mp="12ydv73j014wr">
            <h2 className="text-center text-[clamp(20px,4.4vw,26px)] font-bold leading-[1.2] text-[#050505]" data-id="element-985" data-mp="o2epu0n9xivb">
              Para quién es esto (y para quién no)
            </h2>
            <div className="mt-7 grid gap-4 sm:grid-cols-2" data-id="element-986" data-mp="38s3z7y57kcgm">
              <div className="rounded-xl border border-[#bfe3c6] bg-[#f1faf3] p-5" data-id="element-987" data-mp="38tymvk7v6ioe">
                <p className="text-[15px] font-bold text-[#1a7f37] mb-3" data-id="element-988" data-mp="3dsi9lmua7yht">
                  Es para ti si:
                </p>
                <ul className="space-y-2 text-[14px] text-[#3a3b3c]" data-id="element-989" data-mp="1vv56x6sptjp6">
                  {FOR_YOU.map(item => <li key={item} className="flex items-start gap-2" data-id="element-990" data-mp="24tqkenjby8j3">
                      <CheckIcon className="w-4 h-4 text-[#1a7f37] shrink-0 mt-0.5" data-id="element-991" data-mp="24rw8fyq1dqr5" />
                      {item}
                    </li>)}
                </ul>
              </div>
              <div className="rounded-xl border border-[#f2c2c2] bg-[#fdf2f2] p-5" data-id="element-992" data-mp="1d0q5laixley6">
                <p className="text-[15px] font-bold text-[#c0392b] mb-3" data-id="element-993" data-mp="185aqygd3nf0p">
                  No es para ti si:
                </p>
                <ul className="space-y-2 text-[14px] text-[#3a3b3c]" data-id="element-994" data-mp="1h4frfs881ifi">
                  {NOT_FOR_YOU.map(item => <li key={item} className="flex items-start gap-2" data-id="element-995" data-mp="tebntkv19ioi">
                      <XIcon className="w-4 h-4 text-[#c0392b] shrink-0 mt-0.5" data-id="element-996" data-mp="tch3a7nobsky" />
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="w-full max-w-[760px] mx-auto px-5 py-12" data-id="element-997" data-mp="2tfi64yctneqh">
          <h2 className="text-center text-[clamp(20px,4.4vw,26px)] font-bold leading-[1.2] text-[#050505]" data-id="element-998" data-mp="2y8ukmfnse55i">
            Lo que dicen nuestros miembros
          </h2>
          <div className="mt-7 grid gap-4 sm:grid-cols-3" data-id="element-999" data-mp="25j5ks5mtcb5h">
            {TESTIMONIALS.map((t, i) => <blockquote key={i} className="rounded-xl border border-[#e4e6eb] bg-white p-5 text-[14px] leading-[1.55] text-[#3a3b3c] italic" data-id="element-1000" data-mp="2ahywbsijhs30" data-mpi={i}>
                <div className="flex gap-0.5 mb-2 not-italic" data-id="element-1001" data-mp="2kgeqk4cv5hgq">
                  {Array.from({
                length: 5
              }).map((_, s) => <StarIcon key={s} className="w-3.5 h-3.5 text-[#F5B301] fill-[#F5B301]" data-id="element-1002" data-mp="3qx9y2xr9sfeq" data-mpi={s} />)}
                </div>
                “{t}”
              </blockquote>)}
          </div>
          <p className="mt-5 text-[11px] leading-[1.5] text-[#9aa0a6] text-center max-w-[640px] mx-auto" data-id="element-1003" data-mp="1f9dgv8a061fv">
            Los resultados varían de persona a persona. Estos testimonios
            reflejan experiencias individuales y no representan resultados
            típicos ni garantía de ningún tipo.
          </p>
        </section>

        {/* FAQ */}
        <section className="w-full bg-white" data-id="element-1004" data-mp="10lse5fh1i48h">
          <div className="w-full max-w-[760px] mx-auto px-5 py-12" data-id="element-1005" data-mp="wivpb70wpbw3">
            <h2 className="text-center text-[clamp(20px,4.4vw,26px)] font-bold leading-[1.2] text-[#050505]" data-id="element-1006" data-mp="wkqewyn4hs6i">
              Preguntas frecuentes
            </h2>
            <div className="mt-7 divide-y divide-[#e4e6eb] border border-[#e4e6eb] rounded-xl overflow-hidden" data-id="element-1007" data-mp="mlhmia7wolm9">
              {FAQS.map(f => <details key={f.q} className="group bg-white" data-id="element-1008" data-mp="2lv7mv3z1czla">
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 text-[15px] font-semibold text-[#050505]" data-id="element-1009" data-mp="2gz0fr7uwli1r">
                    {f.q}
                    <span className="text-[#1877F2] transition-transform group-open:rotate-45 text-[20px] leading-none" data-id="element-1010" data-mp="1ur6dqg4xa45u">
                      +
                    </span>
                  </summary>
                  <p className="px-5 pb-4 text-[14px] leading-[1.55] text-[#65676b]" data-id="element-1011" data-mp="1l1d2jg8ozr24">
                    {f.a}
                  </p>
                </details>)}
            </div>
          </div>
        </section>

        {/* CLOSE */}
        <section className="w-full max-w-[760px] mx-auto px-5 py-14 text-center" data-id="element-1012" data-mp="1bzdkcklxygxl">
          <div className="inline-flex items-center gap-2 text-[#1a7f37] text-[13px] font-semibold mb-3" data-id="element-1013" data-mp="x6aaz0ii79hy">
            <ShieldCheckIcon className="w-4 h-4" data-id="element-1014" data-mp="125mzdpp0womn" />
            Pago seguro
          </div>
          <h2 className="font-['Anton'] uppercase tracking-[0.5px] text-[clamp(26px,5.5vw,40px)] leading-[1.05] text-[#050505]" data-id="element-1015" data-mp="2rj9w195nkvwm">
            Empieza hoy por $27
          </h2>
          <p className="mt-3 text-[15px] text-[#65676b]" data-id="element-1016" data-mp="kuztlxjv468o">
            Acceso completo. Un solo pago. Contenido en español.
          </p>
          <div className="mt-6 flex justify-center" data-id="element-1017" data-mp="ftah0dgzrkih">
            <CtaButton onClick={handleCheckout('close')} data-id="element-1018" data-mp="ul2v6m51usim" />
          </div>
          <p className="mt-3 text-[12px] text-[#8a8d91]" data-id="element-1019" data-mp="pssz0odccw7w">
            El precio de lanzamiento sube cuando cierre esta promoción.
          </p>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="w-full bg-[#f0f2f5] border-t border-[#e4e6eb] py-8 text-[#606770]" data-id="element-1020" data-mp="zje4mvrfn10b">
        <div className="max-w-[760px] mx-auto px-5" data-id="element-1021" data-mp="1dinb33pu6gr3">
          <p className="text-center text-[11px] text-[#8a8d91]" data-id="element-1022" data-mp="1didlhh8peblx">
            © 2026 True Marketer LLC. Todos los derechos reservados. [Dirección
            comercial] · [Email de soporte]
          </p>
          <LegalLinks containerClassName="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 mt-3 text-[#606770]" linkClassName="hover:text-[#1877F2] mx-1.5 underline-offset-2 hover:underline" data-id="element-1023" data-mp="3faij99cvw34k" />
          <p className="mt-5 text-[10px] leading-[1.55] text-[#9aa0a6] max-w-[680px] mx-auto text-center" data-id="element-1024" data-mp="11t9fxe18sct1">
            Aviso legal: Este programa es de naturaleza educativa. No
            garantizamos ingresos ni resultados de ningún tipo. Los resultados
            mostrados no son típicos y dependen del esfuerzo, la experiencia, el
            capital disponible y el mercado de cada persona. La mayoría de las
            personas que compran cursos de negocios no obtienen ingresos
            significativos. Cualquier decisión de inversión de tiempo o dinero
            es responsabilidad exclusiva del usuario.
          </p>
        </div>
      </footer>
    </div>;
}